import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiException, TeamRolesAssignmentForm } from './api';

/**
 * Hand-written client for the administrator account-management endpoints: the per-project account
 * record, the "who can open this project" list and bulk invitations.
 *
 * api.ts is NSwag-generated and pinned, so these live here — same constructor shape as a generated
 * *Service, same axios instance, same ApiException (see analyticsApi.ts for the precedent).
 */

export enum LicenceKind {
  Unspecified = 0,
  Paid = 1,
  Trial = 2,
  PatersonAndCookeBundle = 3,
  AbelBundle = 4,
}

/** Dates are calendar days as 'YYYY-MM-DD' strings, with no time or zone. */
export interface LicenceTermDto {
  startDate: string;
  endDate?: string | null;
  cost?: number | null;
  currency?: string | null;
  notes?: string | null;
}

export interface ProjectAccountDto {
  keyContactEmail?: string | null;
  keyContactName?: string | null;
  licenceKind: LicenceKind;
  modelReadyDate?: string | null;
  onboardingSessionDate?: string | null;
  licenceTerms: LicenceTermDto[];
  renewalCount: number;
}

export type SetProjectAccountCommand = Omit<ProjectAccountDto, 'renewalCount'>;

export interface ProjectMemberDto {
  userId: string;
  userName?: string | null;
  email?: string | null;
  forename?: string | null;
  surname?: string | null;
  isEnabled: boolean;
  projectRoles: string[];
  clientRoles: string[];
  /** ISO-8601 UTC timestamp. */
  lastConnectionUtc?: string | null;
}

export interface ProjectPendingInvitationDto {
  invitationId: string;
  email: string;
  /** ISO-8601 UTC timestamp. */
  createdUtc?: string | null;
  projectRoles: string[];
  clientRoles: string[];
}

export interface ProjectAccessDto {
  projectId: string;
  clientId?: string | null;
  clientName?: string | null;
  members: ProjectMemberDto[];
  pendingInvitations: ProjectPendingInvitationDto[];
}

export interface BulkInviteForm {
  emails: string[];
  teamRoles: TeamRolesAssignmentForm[];
  isPatersonAndCookeUser?: boolean;
}

export type BulkInviteStatus = 'Invited' | 'AlreadyInvited' | 'AlreadyMember' | 'InvalidEmail' | 'Failed';

export interface BulkInviteResultDto {
  email: string;
  status: BulkInviteStatus;
  message?: string | null;
}

export class AccountAdminService {
  private instance: AxiosInstance;

  private baseUrl: string;

  constructor(baseUrl: string, instance: AxiosInstance) {
    this.baseUrl = baseUrl;
    this.instance = instance;
  }

  getProjectAccount(projectId: string): Promise<ProjectAccountDto> {
    return this.send<ProjectAccountDto>('GET', `/api/Projects/${encodeURIComponent(projectId)}/account`);
  }

  setProjectAccount(projectId: string, command: SetProjectAccountCommand): Promise<void> {
    return this.send<void>('PUT', `/api/Projects/${encodeURIComponent(projectId)}/account`, command);
  }

  getProjectAccess(projectId: string): Promise<ProjectAccessDto> {
    return this.send<ProjectAccessDto>('GET', `/api/Users/project/${encodeURIComponent(projectId)}/access`);
  }

  bulkInvite(form: BulkInviteForm): Promise<BulkInviteResultDto[]> {
    return this.send<BulkInviteResultDto[]>('POST', '/api/Invitations/bulk', form);
  }

  private send<T>(method: 'GET' | 'PUT' | 'POST', url: string, data?: unknown): Promise<T> {
    const options: AxiosRequestConfig = {
      method,
      url: this.baseUrl + url,
      headers: data
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      data: data ? JSON.stringify(data) : undefined,
    };

    return this.instance
      .request(options)
      // A non-2xx is a resolved response we want to inspect, not a transport failure.
      .catch((error: any) => {
        if (error?.response) {
          return error.response as AxiosResponse;
        }
        throw error;
      })
      .then((response: AxiosResponse) => AccountAdminService.process<T>(response));
  }

  private static process<T>(response: AxiosResponse): T {
    const { status } = response;
    const headers: { [key: string]: any } = {};
    if (response.headers && typeof response.headers === 'object') {
      Object.keys(response.headers).forEach((k) => {
        headers[k] = (response.headers as any)[k];
      });
    }

    // The shared axios instance is created with `transformResponse: (res) => res`, so bodies arrive
    // as raw strings and every client parses its own.
    const text: string = response.data;

    if (status === 200) {
      // The PUT endpoint returns Ok() with an empty body, which JSON.parse rejects.
      return (text ? JSON.parse(text) : undefined) as T;
    }
    if (status === 204) {
      return undefined as unknown as T;
    }

    let result: unknown = null;
    try {
      result = text ? JSON.parse(text) : null;
    } catch {
      // Not a JSON body; the raw text is still on the exception.
    }

    throw new ApiException('A server side error occurred.', status, text, headers, result);
  }
}
